(() => {
  const root = document.documentElement;
  const hero = document.getElementById('hero');
  const ambientCanvas = document.getElementById('heroAmbient');
  const story = document.getElementById('scrollStory');
  const depthStory = story;
  const depthParticles = document.getElementById('depthParticles');
  const depthChapters = [...document.querySelectorAll('.heritage-panel')];
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const coarsePointer = window.matchMedia('(pointer: coarse)').matches;
  const clamp = (value, min, max) => Math.min(max, Math.max(min, value));
  const target = { mx: 0, my: 0, x: 63, y: 43, alpha: coarsePointer ? .34 : .24, erase: 0 };
  const current = { ...target };
  let lastInput = 0;
  let scrollDirty = true;
  let lastScrollY = window.scrollY;
  let scrollVelocity = 0;
  let ambient = null;
  let lastAmbientFrame = 0;

  function compileAmbientShader(gl, type, source) {
    const shader = gl.createShader(type);
    gl.shaderSource(shader, source);
    gl.compileShader(shader);
    if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
      gl.deleteShader(shader);
      return null;
    }
    return shader;
  }

  function initAmbient() {
    if (!ambientCanvas) return;
    const gl = ambientCanvas.getContext('webgl2', {
      alpha: true,
      antialias: false,
      depth: false,
      stencil: false,
      premultipliedAlpha: false,
      powerPreference: 'high-performance'
    });
    if (!gl) return;

    const vertexSource = `#version 300 es
      in vec2 aPosition;
      void main() { gl_Position = vec4(aPosition, 0.0, 1.0); }
    `;
    const fragmentSource = `#version 300 es
      precision highp float;
      uniform vec2 uResolution;
      uniform vec2 uPointer;
      uniform float uTime;
      uniform float uMotion;
      out vec4 outColor;

      float hash21(vec2 p) {
        p = fract(p * vec2(123.34, 456.21));
        p += dot(p, p + 45.32);
        return fract(p.x * p.y);
      }
      float noise(vec2 p) {
        vec2 i = floor(p);
        vec2 f = fract(p);
        f = f * f * (3.0 - 2.0 * f);
        return mix(mix(hash21(i), hash21(i + vec2(1.0, 0.0)), f.x),
                   mix(hash21(i + vec2(0.0, 1.0)), hash21(i + vec2(1.0)), f.x), f.y);
      }
      float fbm(vec2 p) {
        float value = 0.0;
        float amp = 0.5;
        mat2 rot = mat2(0.82, -0.57, 0.57, 0.82);
        for (int i = 0; i < 5; i++) {
          value += amp * noise(p);
          p = rot * p * 2.03 + 7.1;
          amp *= 0.5;
        }
        return value;
      }
      void main() {
        vec2 uv = gl_FragCoord.xy / uResolution.xy;
        vec2 p = uv - 0.5;
        p.x *= uResolution.x / max(uResolution.y, 1.0);
        float t = uTime * 0.12;
        vec2 pointer = (uPointer - 0.5) * vec2(0.34, 0.2);
        float field = fbm(p * 1.65 + vec2(t * 0.22, -t * 0.16) + pointer);
        float fineField = fbm(p * 3.6 - vec2(t * 0.31, t * 0.18));

        float pathA = 0.18 * sin(p.x * 2.5 + t + field * 2.1) + pointer.y;
        float pathB = -0.24 + 0.12 * sin(p.x * 3.1 - t * 0.72 + fineField * 1.7) - pointer.y * 0.45;
        float ribbonA = exp(-abs(p.y - pathA) * 12.0);
        float ribbonB = exp(-abs(p.y - pathB) * 17.0);
        float halo = exp(-length(p - vec2(-0.22 + pointer.x, 0.08)) * 3.8);
        float pulse = 0.82 + 0.18 * sin(t * 1.35 + field * 5.0);

        vec2 dustGrid = uv * vec2(78.0, 48.0);
        vec2 cells = floor(dustGrid);
        vec2 dustLocal = fract(dustGrid) - 0.5;
        float starSeed = hash21(cells);
        float dustShape = 1.0 - smoothstep(0.025, 0.085, length(dustLocal));
        float dust = step(0.979, starSeed) * dustShape * (0.55 + 0.45 * sin(t * 3.0 + starSeed * 24.0));
        dust *= smoothstep(0.0, 0.18, fineField) * 0.68;

        vec3 bronze = vec3(0.76, 0.36, 0.12);
        vec3 gold = vec3(1.0, 0.73, 0.37);
        vec3 pearl = vec3(1.0, 0.9, 0.72);
        float energy = ribbonA * 0.68 + ribbonB * 0.46 + halo * 0.30 + dust * 0.52;
        energy *= mix(0.86, 1.12, uMotion) * pulse;
        vec3 color = mix(bronze, gold, clamp(field + ribbonA * 0.5, 0.0, 1.0));
        color = mix(color, pearl, dust * 0.7 + halo * 0.1);
        float vignette = 1.0 - smoothstep(0.2, 0.94, length(p));
        float alpha = clamp(energy * vignette, 0.0, 0.72);
        outColor = vec4(color * alpha, alpha);
      }
    `;

    const vertex = compileAmbientShader(gl, gl.VERTEX_SHADER, vertexSource);
    const fragment = compileAmbientShader(gl, gl.FRAGMENT_SHADER, fragmentSource);
    if (!vertex || !fragment) return;
    const program = gl.createProgram();
    gl.attachShader(program, vertex);
    gl.attachShader(program, fragment);
    gl.linkProgram(program);
    gl.deleteShader(vertex);
    gl.deleteShader(fragment);
    if (!gl.getProgramParameter(program, gl.LINK_STATUS)) return;

    const vertices = new Float32Array([-1, -1, 3, -1, -1, 3]);
    const vao = gl.createVertexArray();
    const buffer = gl.createBuffer();
    gl.bindVertexArray(vao);
    gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
    gl.bufferData(gl.ARRAY_BUFFER, vertices, gl.STATIC_DRAW);
    const position = gl.getAttribLocation(program, 'aPosition');
    gl.enableVertexAttribArray(position);
    gl.vertexAttribPointer(position, 2, gl.FLOAT, false, 0, 0);
    gl.enable(gl.BLEND);
    gl.blendFunc(gl.ONE, gl.ONE);
    ambient = {
      gl, program, vao,
      resolution: gl.getUniformLocation(program, 'uResolution'),
      pointer: gl.getUniformLocation(program, 'uPointer'),
      time: gl.getUniformLocation(program, 'uTime'),
      motion: gl.getUniformLocation(program, 'uMotion')
    };
    resizeAmbient();
  }

  function resizeAmbient() {
    if (!ambient || !ambientCanvas) return;
    const rect = ambientCanvas.getBoundingClientRect();
    const dpr = Math.min(window.devicePixelRatio || 1, coarsePointer ? 1.15 : 1.55);
    const width = Math.max(1, Math.round(rect.width * dpr));
    const height = Math.max(1, Math.round(rect.height * dpr));
    if (ambientCanvas.width !== width || ambientCanvas.height !== height) {
      ambientCanvas.width = width;
      ambientCanvas.height = height;
      ambient.gl.viewport(0, 0, width, height);
    }
  }

  function drawAmbient(now) {
    if (!ambient || document.hidden) return;
    if (coarsePointer && now - lastAmbientFrame < 32) return;
    if (story.getBoundingClientRect().top < -window.innerHeight * 1.2) return;
    lastAmbientFrame = now;
    const { gl } = ambient;
    gl.useProgram(ambient.program);
    gl.bindVertexArray(ambient.vao);
    gl.uniform2f(ambient.resolution, ambientCanvas.width, ambientCanvas.height);
    gl.uniform2f(ambient.pointer, current.x / 100, 1 - current.y / 100);
    gl.uniform1f(ambient.time, now * .001);
    gl.uniform1f(ambient.motion, clamp(Math.abs(current.mx) + Math.abs(current.my) + current.alpha * .25, 0, 1));
    gl.clearColor(0, 0, 0, 0);
    gl.clear(gl.COLOR_BUFFER_BIT);
    gl.drawArrays(gl.TRIANGLES, 0, 3);
  }

  function updateFromClient(clientX, clientY) {
    const rect = hero.getBoundingClientRect();
    const px = clamp((clientX - rect.left) / rect.width, 0, 1);
    const py = clamp((clientY - rect.top) / rect.height, 0, 1);
    const ringRadius = coarsePointer ? 69 : clamp(window.innerWidth * .09, 75, 140);
    const safeX = clamp(px * 100, ringRadius / rect.width * 100, 100 - ringRadius / rect.width * 100);
    const safeY = clamp(py * 100, ringRadius / rect.height * 100, 100 - ringRadius / rect.height * 100);
    target.mx = (px - .5) * 2;
    target.my = (py - .5) * 2;
    target.x = safeX;
    target.y = safeY;
    current.x = safeX;
    current.y = safeY;
    root.style.setProperty('--spot-x', `${safeX.toFixed(2)}%`);
    root.style.setProperty('--spot-y', `${safeY.toFixed(2)}%`);
    target.alpha = 1;
    target.erase = coarsePointer
      ? clamp(window.innerWidth * .34, 110, 158)
      : clamp(window.innerWidth * .115, 132, 218);
    current.erase = target.erase;
    root.style.setProperty('--erase-size', `${target.erase.toFixed(2)}px`);
    lastInput = performance.now();
  }

  function updatePointer(event) {
    updateFromClient(event.clientX, event.clientY);
  }

  function updateTouch(event) {
    const touch = event.touches[0];
    if (touch) updateFromClient(touch.clientX, touch.clientY);
  }

  function updateTransition() {
    const rect = story.getBoundingClientRect();
    const range = Math.max(1, story.offsetHeight - window.innerHeight);
    const raw = clamp(-rect.top / range, 0, 1);
    const normalized = clamp((raw - .008) / .142, 0, 1);
    const progress = normalized * normalized * (3 - 2 * normalized);
    const diagonal = (window.innerHeight / Math.max(1, window.innerWidth)) * 100;
    const startTop = 112;
    const endBottom = -22;
    const endTop = endBottom - diagonal;
    const top = startTop + (endTop - startTop) * progress;
    const bottom = top + diagonal;
    const bladeWidth = window.innerWidth < 681 ? 18 : 11;

    root.style.setProperty('--wipe-top', `${top.toFixed(3)}%`);
    root.style.setProperty('--wipe-bottom', `${bottom.toFixed(3)}%`);
    root.style.setProperty('--blade-top', `${top.toFixed(3)}%`);
    root.style.setProperty('--blade-bottom', `${bottom.toFixed(3)}%`);
    root.style.setProperty('--blade-top-end', `${(top + bladeWidth).toFixed(3)}%`);
    root.style.setProperty('--blade-bottom-end', `${(bottom + bladeWidth).toFixed(3)}%`);
    root.style.setProperty('--hero-scale', (1 + progress * .055).toFixed(4));
  }

  function updateBridge() {
    const rect = story.getBoundingClientRect();
    const range = Math.max(1, story.offsetHeight - window.innerHeight);
    const storyRaw = clamp(-rect.top / range, 0, 1);
    const raw = clamp((storyRaw - .15) / .28, 0, 1);
    const progress = raw * raw * (3 - 2 * raw);
    const wave = Math.sin(progress * Math.PI);
    const contentOpacity = 1 - clamp((progress - .72) / .25, 0, 1);
    const cardOpacity = 1 - clamp((progress - .75) / .23, 0, 1);
    const holeProgress = clamp((progress - .7) / .3, 0, 1);
    const aperture = .02 + Math.pow(holeProgress, 2.2) * 5;
    const apertureOpacity = clamp((progress - .68) / .1, 0, 1);

    root.style.setProperty('--bridge-aperture', aperture.toFixed(4));
    root.style.setProperty('--bridge-aperture-opacity', apertureOpacity.toFixed(3));
    root.style.setProperty('--portal-radius', `${(aperture * 21).toFixed(3)}vmax`);
    root.style.setProperty('--portal-opacity', apertureOpacity.toFixed(3));
    root.style.setProperty('--bridge-content-opacity', contentOpacity.toFixed(3));
    root.style.setProperty('--bridge-card-opacity', cardOpacity.toFixed(3));
    root.style.setProperty('--bridge-marquee-x', `${(-4 - progress * 34).toFixed(2)}vw`);
    root.style.setProperty('--bridge-a-x', `${(-31 + progress * 23).toFixed(2)}vw`);
    root.style.setProperty('--bridge-a-y', `${(-7 + wave * 8).toFixed(2)}vh`);
    root.style.setProperty('--bridge-a-z', `${(-60 + progress * 350).toFixed(2)}px`);
    root.style.setProperty('--bridge-a-ry', `${(18 - progress * 78).toFixed(2)}deg`);
    root.style.setProperty('--bridge-b-x', `${(Math.sin(progress * Math.PI * 2) * 3).toFixed(2)}vw`);
    root.style.setProperty('--bridge-b-y', `${(18 - progress * 13).toFixed(2)}vh`);
    root.style.setProperty('--bridge-b-z', `${(90 + progress * 380).toFixed(2)}px`);
    root.style.setProperty('--bridge-c-x', `${(31 - progress * 23).toFixed(2)}vw`);
    root.style.setProperty('--bridge-c-y', `${(-5 - wave * 7).toFixed(2)}vh`);
    root.style.setProperty('--bridge-c-z', `${(-40 + progress * 330).toFixed(2)}px`);
    root.style.setProperty('--bridge-c-ry', `${(-18 + progress * 76).toFixed(2)}deg`);
  }

  function updateDepth() {
    if (!depthStory) return;
    const rect = depthStory.getBoundingClientRect();
    const range = Math.max(1, depthStory.offsetHeight - window.innerHeight);
    const storyRaw = clamp(-rect.top / range, 0, 1);
    const raw = clamp((storyRaw - .43) / .57, 0, 1);
    const progress = raw * raw * (3 - 2 * raw);
    const entry = clamp(raw / .12, 0, 1);
    const apertureRelease = clamp(raw / .035, 0, 1);
    const mobile = window.innerWidth < 681;
    const wave = Math.sin(progress * Math.PI);
    const rigY = Math.sin(progress * Math.PI * 2) * -18;
    const rigRX = -9 + wave * 12;
    const rigRY = -24 + progress * 132;
    const rigRZ = -3 + progress * 6 + scrollVelocity * .07;
    const rigScale = (mobile ? .88 : .92) + wave * .075;
    const spread = mobile ? 72 : 125;

    root.style.setProperty('--depth-progress', `${(progress * 100).toFixed(2)}%`);
    root.style.setProperty('--depth-entry-opacity', (.72 + entry * .28).toFixed(3));
    root.style.setProperty('--depth-entry-y', `${((1 - entry) * 28).toFixed(2)}px`);
    root.style.setProperty('--depth-entry-scale', (.92 + entry * .08).toFixed(4));
    root.style.setProperty('--depth-curtain-opacity', `${((1 - entry) * .74).toFixed(3)}`);
    root.style.setProperty('--depth-curtain-x', `${(entry * 34).toFixed(2)}%`);
    if (storyRaw >= .43) root.style.setProperty('--bridge-aperture-opacity', (1 - apertureRelease).toFixed(3));
    root.style.setProperty('--depth-light-x', `${(62 + progress * 24).toFixed(2)}%`);
    root.style.setProperty('--particle-shift', `${(-progress * 78).toFixed(2)}px`);
    root.style.setProperty('--rig-y', `${rigY.toFixed(2)}px`);
    root.style.setProperty('--rig-rx', `${rigRX.toFixed(2)}deg`);
    root.style.setProperty('--rig-ry', `${rigRY.toFixed(2)}deg`);
    root.style.setProperty('--rig-rz', `${rigRZ.toFixed(2)}deg`);
    root.style.setProperty('--rig-scale', rigScale.toFixed(4));
    root.style.setProperty('--halo-r', `${(progress * 250).toFixed(2)}deg`);
    root.style.setProperty('--patch-a-x', `${((mobile ? -108 : -185) + progress * spread).toFixed(2)}px`);
    root.style.setProperty('--patch-a-y', `${((mobile ? -80 : -120) + wave * 38).toFixed(2)}px`);
    root.style.setProperty('--patch-a-z', `${(80 + wave * 225).toFixed(2)}px`);
    root.style.setProperty('--patch-a-r', `${(-18 + progress * 82).toFixed(2)}deg`);
    root.style.setProperty('--patch-b-x', `${((mobile ? 105 : 190) - progress * spread).toFixed(2)}px`);
    root.style.setProperty('--patch-b-y', `${((mobile ? 72 : 105) - wave * 58).toFixed(2)}px`);
    root.style.setProperty('--patch-b-z', `${(40 + wave * 250).toFixed(2)}px`);
    root.style.setProperty('--patch-b-r', `${(24 - progress * 92).toFixed(2)}deg`);

    const phase = progress * Math.max(1, depthChapters.length - 1);
    depthChapters.forEach((chapter, index) => {
      const delta = phase - index;
      const opacity = clamp(1 - Math.abs(delta), 0, 1);
      chapter.style.opacity = opacity.toFixed(3);
      chapter.style.visibility = opacity > .01 ? 'visible' : 'hidden';
      chapter.style.zIndex = `${Math.round(opacity * 100)}`;
      chapter.style.transform = `translate3d(0, ${(-delta * 34).toFixed(2)}px, 0)`;
      chapter.style.filter = `blur(${((1 - opacity) * 2.2).toFixed(2)}px)`;
      chapter.style.setProperty('--heritage-image-y', `${(delta * 18).toFixed(2)}px`);
      chapter.style.setProperty('--heritage-image-scale', (1.055 + (1 - opacity) * .025).toFixed(4));
    });
  }

  if (reduceMotion) return;

  initAmbient();

  hero.addEventListener('pointermove', updatePointer, { passive: true });
  hero.addEventListener('pointerdown', updatePointer, { passive: true });
  hero.addEventListener('pointerenter', updatePointer, { passive: true });
  hero.addEventListener('touchstart', updateTouch, { passive: true });
  hero.addEventListener('touchmove', updateTouch, { passive: true });
  hero.addEventListener('touchend', () => {
    target.alpha = .46;
    target.erase = 0;
    lastInput = performance.now();
  }, { passive: true });
  hero.addEventListener('pointercancel', () => { target.erase = 0; }, { passive: true });
  hero.addEventListener('pointerleave', () => {
    target.mx = 0;
    target.my = 0;
    target.x = 63;
    target.y = 43;
    target.alpha = .3;
    target.erase = 0;
  }, { passive: true });

  window.addEventListener('scroll', () => {
    const nextY = window.scrollY;
    scrollVelocity = clamp(nextY - lastScrollY, -58, 58);
    lastScrollY = nextY;
    scrollDirty = true;
  }, { passive: true });
  window.addEventListener('resize', () => {
    scrollDirty = true;
    resizeAmbient();
  }, { passive: true });

  function render(now) {
    if (scrollDirty) {
      updateTransition();
      updateBridge();
      updateDepth();
      scrollDirty = false;
    }
    const idleDelay = coarsePointer ? 850 : 1350;
    const idleStrength = clamp((now - lastInput - idleDelay) / 1200, 0, 1);
    if (idleStrength > 0) {
      const driftX = Math.sin(now * .00031) * .17 + Math.sin(now * .00013 + 1.4) * .055;
      const driftY = Math.cos(now * .00027 + .7) * .095 + Math.sin(now * .00011) * .035;
      target.mx = driftX * idleStrength;
      target.my = driftY * idleStrength;
      target.x = 61 + Math.sin(now * .00023 + .4) * 11 * idleStrength;
      target.y = 42 + Math.cos(now * .00019) * 7 * idleStrength;
      target.alpha = .38 + (Math.sin(now * .00042) * .5 + .5) * .13;
      target.erase = 0;
    }
    current.mx += (target.mx - current.mx) * .075;
    current.my += (target.my - current.my) * .075;
    current.x += (target.x - current.x) * .095;
    current.y += (target.y - current.y) * .095;
    current.alpha += (target.alpha - current.alpha) * .07;
    current.erase += (target.erase - current.erase) * .14;
    scrollVelocity *= .88;

    root.style.setProperty('--bg-x', `${(-current.mx * 6).toFixed(2)}px`);
    root.style.setProperty('--bg-y', `${(-current.my * 4).toFixed(2)}px`);
    root.style.setProperty('--photo-scale', (1.025 + Math.sin(now * .00038) * .002 * idleStrength).toFixed(5));
    root.style.setProperty('--bronze-x', `${(-current.mx * 6).toFixed(2)}px`);
    root.style.setProperty('--bronze-y', `${(-current.my * 4).toFixed(2)}px`);
    root.style.setProperty('--title-x', `${(current.mx * 9).toFixed(2)}px`);
    root.style.setProperty('--title-y', `${(current.my * 5).toFixed(2)}px`);
    root.style.setProperty('--type-depth-x', `${(current.mx * 3.4).toFixed(2)}px`);
    root.style.setProperty('--type-depth-y', `${(current.my * 2.6).toFixed(2)}px`);
    root.style.setProperty('--type-tilt-x', `${(-current.my * .9).toFixed(3)}deg`);
    root.style.setProperty('--type-tilt-y', `${(current.mx * 1.15).toFixed(3)}deg`);
    root.style.setProperty('--person-x', `${(-current.mx * 6).toFixed(2)}px`);
    root.style.setProperty('--person-y', `${(-current.my * 4).toFixed(2)}px`);
    root.style.setProperty('--front-x', `${(current.mx * 12).toFixed(2)}px`);
    root.style.setProperty('--front-y', `${(current.my * 7).toFixed(2)}px`);
    root.style.setProperty('--spot-x', `${current.x.toFixed(2)}%`);
    root.style.setProperty('--spot-y', `${current.y.toFixed(2)}%`);
    root.style.setProperty('--spot-alpha', current.alpha.toFixed(3));
    root.style.setProperty('--erase-size', `${current.erase.toFixed(2)}px`);
    root.style.setProperty('--velocity-skew', `${(scrollVelocity * .055).toFixed(3)}deg`);
    root.style.setProperty('--velocity-shift', `${(scrollVelocity * .42).toFixed(2)}px`);
    drawAmbient(now);
    requestAnimationFrame(render);
  }

  updateTransition();
  updateBridge();
  updateDepth();
  requestAnimationFrame(render);
})();
